import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG, API_ENDPOINTS } from 'config/api.config';
import { storage, notify } from 'utils/helpers';
import { HTTP_STATUS } from 'utils/constants';
import { ErrorResponse } from 'types/api.types';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = storage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        method: config.method,
        url: config.url,
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError<ErrorResponse>) => {
    if (error.code === AxiosError.ERR_CANCELED) {
      return Promise.reject(error);
    }

    const originalRequest: any = error.config;

    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: originalRequest?.url,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${API_CONFIG.baseURL}${API_ENDPOINTS.auth.refresh}`,
          {},
          { withCredentials: true }
        );

        const { access_token } = response.data.tokens;

        storage.setToken(access_token);

        apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        storage.clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response) {
      switch (error.response.status) {
        case HTTP_STATUS.FORBIDDEN:
          notify.error('У вас нет прав для выполнения этого действия');
          break;
        case HTTP_STATUS.NOT_FOUND:
          notify.error('Запрашиваемый ресурс не найден');
          break;
        case HTTP_STATUS.TOO_MANY_REQUESTS:
          notify.error('Слишком много запросов. Пожалуйста, попробуйте позже');
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        case HTTP_STATUS.BAD_GATEWAY:
        case HTTP_STATUS.SERVICE_UNAVAILABLE:
          notify.error('Ошибка сервера. Пожалуйста, попробуйте позже');
          break;
        default:
          if (error.response.data?.error) {
            notify.error(error.response.data.error);
          }
      }
    } else if (error.request) {
      notify.error('Ошибка сети. Пожалуйста, проверьте подключение к интернету');
    } else {
      notify.error('Произошла непредвиденная ошибка');
    }

    return Promise.reject(error);
  }
);

export const publicClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true,
});

export const api = {
  get: <T = any>(url: string, params?: any, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, { ...config, params }),

  post: <T = any>(url: string, data?: any, config?: any) =>
    apiClient.post<T>(url, data, config),

  put: <T = any>(url: string, data?: any) =>
    apiClient.put<T>(url, data),

  patch: <T = any>(url: string, data?: any) =>
    apiClient.patch<T>(url, data),

  delete: <T = any>(url: string, params?: any) =>
    apiClient.delete<T>(url, { params }),

  upload: <T = any>(url: string, file: File, fieldName: string = 'file', onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append(fieldName, file);

    return apiClient.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
  },

  download: (url: string, filename?: string) => {
    return apiClient.get(url, {
      responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    });
  },
};

export default apiClient;